import { logger } from "@/utils/logger";
import { DealerAuthService, DealerConfig, DealerAuthRequest } from './dealerAuth';
import { DealerCache } from './dealerCache';

/**
 * Main dealer management class - orchestrates authentication, caching, and API key access
 */
export class DealerManager {
  /**
   * In-flight authentication requests, keyed by the effective request identity.
   *
   * Eight separate call sites (widget.tsx, DealerSessionContext, secured-client,
   * useAppInitialization, useChatCleanup, useOpenAIResponses, dealerDataUtils,
   * inventoryUtils) can race into authentication on a single page load. The old
   * DealerCache.isLoading guard could not stop this: it is polled on a 500ms
   * timer, and two callers that both read isLoading === false before either
   * calls setLoading(true) each proceed to hit the network. That produced ~2
   * dealer-auth invocations (and 2 beacons) per session.
   *
   * Sharing the actual promise makes concurrent callers collapse onto one
   * request deterministically, with no polling.
   */
  private static inFlightAuth = new Map<string, Promise<DealerConfig>>();

  private static authKey(request: DealerAuthRequest): string {
    if (typeof request.dealer_id === 'number' && request.dealer_id > 0) {
      return `id:${request.dealer_id}:${request.testMode ? 1 : 0}`;
    }
    return `domain:${(request.domain || '').toLowerCase()}:${request.testMode ? 1 : 0}`;
  }

  /**
   * Authenticate dealer and get configuration.
   *
   * IMPORTANT: Dealer settings can change frequently (e.g., feature flags like trade_banner_enabled),
   * so we treat the network response as the source of truth and only fall back to cache if the
   * auth request fails.
   */
  static async authenticateDealer(request: DealerAuthRequest): Promise<DealerConfig> {
    const key = this.authKey(request);
    const existing = this.inFlightAuth.get(key);
    if (existing) {
      logger.log('Authentication already in progress — reusing in-flight request');
      return existing;
    }

    const promise = this.performAuthentication(request).finally(() => {
      this.inFlightAuth.delete(key);
    });
    this.inFlightAuth.set(key, promise);
    return promise;
  }

  private static async performAuthentication(request: DealerAuthRequest): Promise<DealerConfig> {
    const cachedConfig = DealerCache.get();

    const normalizeDomain = (d: string) =>
      d
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .trim();

    const requestedDealerId = request.dealer_id;
    const requestedDomain = request.domain;

    const cacheMatchesRequest = cachedConfig
      ? (typeof requestedDealerId === 'number' && requestedDealerId > 0)
        ? cachedConfig.dealer_id === requestedDealerId
        : (typeof requestedDomain === 'string' && requestedDomain.trim())
          ? normalizeDomain(cachedConfig.domain) === normalizeDomain(requestedDomain)
          : true
      : false;

    DealerCache.setLoading(true);

    try {
      const config = await DealerAuthService.authenticate(request);
      DealerCache.set(config);
      return config;
    } catch (error) {
      // If auth fails (network/edge), fall back to cache *only* if it matches the current request.
      if (cachedConfig && cacheMatchesRequest) {
        logger.warn('⚠️ Dealer auth failed; falling back to cached dealer config');
        return cachedConfig;
      }
      throw error;
    } finally {
      DealerCache.setLoading(false);
    }
  }

  /**
   * Validate if dealer exists (without sensitive data)
   */
  static async validateDealer(dealer_id?: number, domain?: string): Promise<boolean> {
    return DealerAuthService.validateDealer(dealer_id, domain);
  }

  /**
   * Get current dealer configuration (cached)
   */
  static getCurrentDealer(): DealerConfig | null {
    return DealerCache.get();
  }

  /**
   * Get current dealer ID (cached)
   */
  static getCurrentDealerId(): number | null {
    const dealer = DealerCache.get();
    return dealer?.dealer_id || null;
  }

  /**
   * Clear cached dealer configuration
   */
  static clearCache(): void {
    logger.log('Clearing dealer cache');
    DealerCache.clear();
  }

  /**
   * Debug current dealer state (useful for troubleshooting)
   */
  static debugCurrentState(): void {
    logger.log('=== DEALER DEBUG STATE ===');
    const config = DealerCache.get();
    const stats = DealerCache.getStats();
    
    logger.log('Cache Stats:', stats);
    logger.log('Current Config:', config ? {
      dealer_id: config.dealer_id,
      domain: config.domain,
      has_assistant_id: !!config.openai_assistant_id,
      assistant_id: config.openai_assistant_id || 'none'
    } : 'No config cached');
    logger.log('Global Settings:', (window as any).D2CChatbotSettings);
    logger.log('=== END DEALER DEBUG ===');
  }

  /**
   * SECURITY: API key methods removed - API keys are handled securely on the backend only
   * Frontend code should never have access to API keys
   */

  /**
   * Get OpenAI Assistant ID for current dealer with auto-authentication
   */
  static async getOpenAIAssistantId(): Promise<string> {
    logger.log('Getting OpenAI Assistant ID...');
    
    // Try cache first to avoid frequent re-authentication
    const cachedConfig = DealerCache.get();
    if (cachedConfig?.openai_assistant_id) {
      // Using cached Assistant ID
      return cachedConfig.openai_assistant_id;
    }
    
    logger.log('No cached Assistant ID found, re-authenticating...');
    
    const config = await this.ensureAuthenticated();
    
    if (!config.openai_assistant_id) {
      console.error('OpenAI Assistant ID not configured for dealer:', config.dealer_id);
      throw new Error('OpenAI Assistant ID not configured for this dealer.');
    }
    
    // OpenAI Assistant ID retrieved
    return config.openai_assistant_id;
  }


  /**
   * Check if sales pages only mode is enabled
   */
  static async isSalesPagesOnly(): Promise<boolean> {
    const cachedConfig = DealerCache.get();
    if (cachedConfig?.sales_pages_only !== undefined) {
      return cachedConfig.sales_pages_only;
    }
    
    const config = await this.ensureAuthenticated();
    return config.sales_pages_only || false;
  }

  /**
   * Get dealer name for current dealer
   */
  static async getDealerName(): Promise<string> {
    logger.log('Getting dealer name...');
    
    const cachedConfig = DealerCache.get();
    if (cachedConfig?.dealer_name !== undefined) {
      // Using cached dealer name
      return cachedConfig.dealer_name;
    }
    
    logger.log('No cached dealer name found, re-authenticating...');
    const config = await this.ensureAuthenticated();
    
    const dealerName = config.dealer_name || '';
    // Dealer name retrieved
    return dealerName;
  }

  /**
   * Get welcome message for current dealer based on language
   */
  static async getWelcomeMessage(language: 'en' | 'fr'): Promise<string> {
    const cachedConfig = DealerCache.get();
    if (cachedConfig) {
      const welcomeMessage = language === 'fr' ? cachedConfig.welcome_message_fr : cachedConfig.welcome_message_en;
      const dealerName = cachedConfig.dealer_name || (language === 'fr' ? 'notre concessionnaire' : 'our dealership');
      
      if (welcomeMessage && welcomeMessage.trim() !== '') {
        // Using cached welcome message
        return welcomeMessage.replace('{dealer_name}', dealerName);
      }
      
      // If no custom welcome message, return a default with dealer name
      const defaultMessages = {
        fr: `Bonjour, bienvenue chez ${dealerName}! Je suis votre chatbot IA. Demandez-moi n'importe quoi!`,
        en: `Good evening! Welcome to ${dealerName}. You can ask me anything!`
      };
      
      // Using default welcome message
      return defaultMessages[language];
    }
    
    logger.log('No cached config found, re-authenticating...');
    const config = await this.ensureAuthenticated();
    
    const welcomeMessage = language === 'fr' ? config.welcome_message_fr : config.welcome_message_en;
    const dealerName = config.dealer_name || (language === 'fr' ? 'notre concessionnaire' : 'our dealership');
    
    if (welcomeMessage && welcomeMessage.trim() !== '') {
      const processedMessage = welcomeMessage.replace('{dealer_name}', dealerName);
      // Welcome message retrieved
      return processedMessage;
    }
    
    // Fallback to default messages if not configured
    const defaultMessages = {
      fr: `Bonjour, bienvenue chez ${dealerName}! Je suis votre chatbot IA. Demandez-moi n'importe quoi!`,
      en: `Good evening! Welcome to ${dealerName}. You can ask me anything!`
    };
    
    // Using default welcome message
    return defaultMessages[language];
  }

  /**
   * Initialize dealer configuration from global settings
   */
  static async initializeFromGlobalSettings(): Promise<DealerConfig> {
    // Concurrent callers are collapsed onto a single request by the in-flight
    // promise map in authenticateDealer(); the old 500ms isLoading poll here
    // could not prevent duplicate network calls and is no longer needed.

    
    // Check for test mode override (URL param or cookie)
    const urlParams = new URLSearchParams(window.location.search);
    const hasTestModeParam = urlParams.get('chatbottest') === 'true';

    // Optional: force-refresh dealer config on demand (useful when toggling flags like stream=true)
    const hasNoCacheParam = urlParams.has('nocache') || urlParams.get('nocache') === 'true';
    if (hasNoCacheParam) {
      logger.log('🧹 nocache param detected - forcing dealer cache refresh');
      DealerCache.forceRefresh();
    }
    
    // Import cookie utilities
    const { setChatbotTestMode, isChatbotTestMode } = await import('./cookieUtils');
    
    // If URL param is present, set the cookie for persistence
    if (hasTestModeParam) {
      setChatbotTestMode();
    }
    
    // Test mode is enabled if either URL param or cookie is present
    const isTestMode = hasTestModeParam || isChatbotTestMode();
    
    try {
      // Check for global D2C chatbot settings
      const globalSettings = (window as any).D2CChatbotSettings;
      
      if (globalSettings?.dealerid) {
        try {
          const config = await this.authenticateDealer({
            dealer_id: globalSettings.dealerid,
            origin: window.location.origin,
            testMode: isTestMode
          });
          return config;
        } catch (authError) {
          console.error('Dealer authentication failed for ID:', globalSettings.dealerid, 'Error:', authError);
          // Check if it's an unauthorized domain error
          if (authError instanceof Error && authError.message.includes('UNAUTHORIZED_DOMAIN')) {
            console.error('CRITICAL: Unauthorized domain detected, blocking app initialization');
            throw new Error('UNAUTHORIZED_DOMAIN: This domain is not authorized to use this application');
          }
          throw authError;
        }
      }

      // Check for test dealer override in URL params (for development/admin preview)
      const testDealerId = urlParams.get('test_dealer_id');
      if (testDealerId) {
        const dealerId = parseInt(testDealerId, 10);
        if (!isNaN(dealerId) && dealerId > 0) {
          try {
            // Always use testMode when test_dealer_id is specified
            return await this.authenticateDealer({
              dealer_id: dealerId,
              origin: window.location.origin,
              testMode: true
            });
          } catch (authError) {
            console.error('Test dealer authentication failed for ID:', dealerId, 'Error:', authError);
            // Continue to domain-based auth as fallback
          }
        }
      }

      // Use domain-based authentication as fallback
      const domain = window.location.hostname;
      try {
        return await this.authenticateDealer({
          domain: domain,
          origin: window.location.origin,
          testMode: isTestMode
        });
      } catch (authError) {
        console.error('Domain-based authentication failed for:', domain, 'Error:', authError);
        // Check if it's an unauthorized domain error
        if (authError instanceof Error && authError.message.includes('UNAUTHORIZED_DOMAIN')) {
          console.error('CRITICAL: Unauthorized domain detected, blocking app initialization');
          throw new Error('UNAUTHORIZED_DOMAIN: This domain is not authorized to use this application');
        }
        throw authError;
      }
    } catch (error) {
      console.error('Failed to initialize dealer configuration:', error);
      // If it's an unauthorized domain, don't retry - just fail completely
      if (error instanceof Error && error.message.includes('UNAUTHORIZED_DOMAIN')) {
        throw error;
      }
      throw new Error(`Failed to authenticate dealer: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if dealer configuration is available
   */
  static isConfigured(): boolean {
    return DealerCache.get() !== null;
  }

  /**
   * Get dealer info (non-sensitive data)
   */
  static getDealerInfo(): { dealer_id: number; domain: string } | null {
    const config = DealerCache.get();
    if (!config) return null;
    
    return {
      dealer_id: config.dealer_id,
      domain: config.domain
    };
  }

  /**
   * Check if TradeBoost is enabled for current dealer
   */
  static isTradeBoostEnabled(): boolean {
    const config = DealerCache.get();
    return config?.tradeboost || false;
  }

  /**
   * Check if popular questions are enabled for current dealer
   */
  static isPopularQuestionsEnabled(): boolean {
    const config = DealerCache.get();
    return config?.popular_questions_enabled === true;
  }

  /**
   * Check if trade banner is enabled for current dealer
   */
  static isTradeBannerEnabled(): boolean {
    const config = DealerCache.get();
    // Strict check: only show when the backend explicitly says "true"
    return config?.trade_banner_enabled === true;
  }

  /**
   * Check if payments are enabled for current dealer
   */
  static isPaymentsEnabled(): boolean {
    const config = DealerCache.get();
    return config?.payments === true;
  }

  /**
   * Check if streaming is enabled for current dealer
   */
  static isStreamEnabled(): boolean {
    const config = DealerCache.get();
    return config?.stream === true;
  }

  /**
   * Get vector store ID for current dealer
   */
  static getVectorStoreId(): string | null {
    const config = DealerCache.get();
    return config?.vector_store_id || null;
  }

  /**
   * Ensure dealer is authenticated, auto-authenticate if needed with retry logic
   */
  private static async ensureAuthenticated(): Promise<DealerConfig> {
    // Check cache first
    let config = DealerCache.get();
    if (config) return config;
    
    // Auto-authenticate with retry logic
    const maxRetries = 2;
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        logger.log(`Authentication attempt ${attempt}/${maxRetries}`);
        config = await this.initializeFromGlobalSettings();
        logger.log('Authentication successful on attempt', attempt);
        
        
        return config;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        console.error(`Authentication attempt ${attempt} failed:`, lastError.message);
        
        if (attempt < maxRetries) {
          // Clear any corrupted cache before retry
          DealerCache.clear();
          logger.log('Cleared cache before retry');
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
    
    throw new Error(`Auto-authentication failed after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
  }

  /**
   * Force refresh dealer configuration (useful for error recovery)
   */
  static async forceRefresh(): Promise<DealerConfig> {
    logger.log('Force refreshing dealer configuration...');
    DealerCache.clear();
    return await this.ensureAuthenticated();
  }
}

// Export types for backward compatibility
export type { DealerConfig, DealerAuthRequest };